import { FormControlLabel, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/components/ui/alert/alert';
import { Checkbox } from '@/shared/components/ui/checkbox/checkbox';
import { ConfirmDelete } from '@/shared/components/ui/confirm-delete/confirm-delete';
import { Can } from '@/shared/features/permissions/components/can/can';
import { TDeleteRolesProps } from '@/shared/features/rbac/components/delete-role/delete-role.types';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useDeleteRoleMutation } from '@/shared/redux/api/ident/ident-api';
import { DeleteRoleMutation } from '@/shared/types/types.generated';

export const DeleteRole: React.FC<TDeleteRolesProps> = ({
  userRoleIds,
  onSuccess,
  component = 'MenuItem',
  disabled,
  permission,
}) => {
  const { t } = useTranslation();
  const { permissionActions, supportsForceRoleDeletion } = useRbacConfig();
  const [deleteRoleMutation] = useDeleteRoleMutation();
  const [force, setForce] = useState(false);

  const count = userRoleIds.length;

  const deleteRole = async () => {
    const promises: Promise<DeleteRoleMutation>[] = [];

    userRoleIds.forEach((userRoleId) => {
      promises.push(deleteRoleMutation({ userRoleId, force }).unwrap());
    });
    await Promise.all(promises);
  };

  const renderForceDelete = () => {
    return (
      <Stack gap="0.5rem" width="100%">
        <FormControlLabel
          control={
            <Checkbox
              checked={force}
              onChange={(_, checked) => setForce(checked)}
            />
          }
          label={
            <Typography variant="p5" color="textSecondary">
              {t('rbac:force_delete_role')}
            </Typography>
          }
        />
        {force && (
          <Alert
            color="yellow"
            text={t('rbac:force_delete_role_hint', { count })}
          />
        )}
      </Stack>
    );
  };

  return (
    <Can action={permissionActions.deleteUserRole} ids={userRoleIds}>
      <ConfirmDelete
        component={component}
        deleteMutation={deleteRole}
        itemCount={count}
        deleteWarning={supportsForceRoleDeletion && renderForceDelete()}
        onSuccess={onSuccess}
        onClose={() => setForce(false)}
        disabled={disabled}
        permission={permission}
      />
    </Can>
  );
};
